import { Link } from "react-router-dom"
import {
  IconFilter,
  IconUsers,
  IconClock,
  IconShare,
  IconArrowRight,
  IconSparkles,
  IconLock,
} from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { PageContainer } from "@/components/PageContainer"
import { cn } from "@/lib/utils"
import { fetchWithAuth } from "@/data/api/client"

function notifyUpgradeInterest() {
  fetchWithAuth('/api/v1/slack-notifications', {
    method: 'POST',
    body: JSON.stringify({ action_type: 'INTEREST_FOR_NON_FREE_FEATURES' }),
  }).catch(() => {
    // Silently ignore errors - this is a non-critical tracking request
  })
}

interface FeatureCardProps {
  icon: React.ReactNode
  title: string
  description: string
  delay?: number
}

function FeatureCard({ icon, title, description, delay = 0 }: FeatureCardProps) {
  return (
    <div
      className={cn(
        "group relative p-[24px] rounded-[12px] border border-[#EDEEF3] bg-white",
        "hover:border-[#654EDA]/30 hover:shadow-[0_8px_32px_rgba(101,78,218,0.08)]",
        "transition-all duration-300 ease-out",
        "animate-in fade-in slide-in-from-bottom-4"
      )}
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'backwards' }}
    >
      <div className="flex items-start gap-[16px]">
        <div className={cn(
          "flex items-center justify-center size-[44px] rounded-[10px]",
          "bg-gradient-to-br from-[#654EDA]/10 to-[#654EDA]/5",
          "group-hover:from-[#654EDA]/20 group-hover:to-[#654EDA]/10",
          "transition-all duration-300"
        )}>
          <span className="text-[#654EDA]">{icon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-medium text-[#08090A] mb-[6px]">{title}</h3>
          <p className="text-[13px] text-[#8A8F98] leading-[20px]">{description}</p>
        </div>
      </div>
    </div>
  )
}

interface StatCardProps {
  value: string
  label: string
  delay?: number
}

function StatCard({ value, label, delay = 0 }: StatCardProps) {
  return (
    <div
      className={cn(
        "text-center p-[20px]",
        "animate-in fade-in zoom-in-95"
      )}
      style={{ animationDelay: `${delay}ms`, animationFillMode: 'backwards' }}
    >
      <div className="text-[32px] font-semibold text-[#654EDA] mb-[4px]">{value}</div>
      <div className="text-[13px] text-[#8A8F98]">{label}</div>
    </div>
  )
}

export function CustomDashboardUpgradePage() {
  return (
    <PageContainer maxWidth="900px" className="lg:py-[48px]">
      {/* Hero Section */}
      <div className="text-center mb-[56px] animate-in fade-in slide-in-from-bottom-6 duration-500">
        {/* Badge */}
        <div className="inline-flex items-center gap-[8px] px-[14px] py-[6px] rounded-full bg-[#654EDA]/10 border border-[#654EDA]/20 mb-[24px]">
          <IconLock className="size-3.5 text-[#654EDA]" />
          <span className="text-[12px] font-medium text-[#654EDA]">Starter Plan Feature</span>
        </div>

        {/* Headline */}
        <h1 className="text-[36px] font-semibold text-[#08090A] leading-[1.2] mb-[16px]">
          Build dashboards that match<br />
          <span className="bg-gradient-to-r from-[#654EDA] to-[#9B59B6] bg-clip-text text-transparent">
            how your team thinks
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-[16px] text-[#8A8F98] leading-[26px] max-w-[560px] mx-auto mb-[32px]">
          Create purpose-built views for every stakeholder. From engineering leads tracking model efficiency
          to finance teams monitoring cost allocation—everyone gets the insights they need.
        </p>

        {/* CTA */}
        <div className="flex items-center justify-center gap-[12px]">
          <Link to="/organization/billing">
            <Button
              size="lg"
              className="h-[44px] px-[24px] bg-[#654EDA] hover:bg-[#654EDA]/90 text-white font-medium rounded-[8px] shadow-[0_4px_12px_rgba(101,78,218,0.25)] hover:shadow-[0_6px_20px_rgba(101,78,218,0.35)] transition-all duration-300"
              onClick={notifyUpgradeInterest}
            >
              <IconSparkles className="size-4 mr-[8px]" />
              Upgrade to Starter
              <IconArrowRight className="size-4 ml-[8px]" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Bar */}
      <div
        className="grid grid-cols-3 bg-gradient-to-r from-[#F7F8F8] via-white to-[#F7F8F8] rounded-[12px] border border-[#EDEEF3] mb-[48px] animate-in fade-in slide-in-from-bottom-4 duration-500"
        style={{ animationDelay: '100ms', animationFillMode: 'backwards' }}
      >
        <StatCard value="73%" delay={200} label="Faster insight discovery" />
        <div className="border-x border-[#EDEEF3]">
          <StatCard value="4.2×" delay={300} label="More actionable reports" />
        </div>
        <StatCard value="∞" delay={400} label="Custom dashboard configurations" />
      </div>

      {/* Feature Grid */}
      <div className="mb-[48px]">
        <h2 className="text-[18px] font-medium text-[#08090A] text-center mb-[24px] animate-in fade-in slide-in-from-bottom-4 duration-500" style={{ animationDelay: '200ms', animationFillMode: 'backwards' }}>
          Everything you need to take control of AI costs
        </h2>

        <div className="grid grid-cols-2 gap-[16px]">
          <FeatureCard
            icon={<IconFilter className="size-5" />}
            title="Advanced Filtering"
            description="Slice data by team, project, model, or any custom metadata. Zero in on exactly what matters."
            delay={250}
          />
          {/* <FeatureCard
            icon={<IconChartBar className="size-5" />}
            title="Flexible Visualizations"
            description="Choose from area charts, bar charts, pie charts, and tables. Present data the way stakeholders prefer."
            delay={300}
          /> */}
          <FeatureCard
            icon={<IconUsers className="size-5" />}
            title="Team Collaboration"
            description="Share dashboards across your organization. Keep everyone aligned on cost optimization goals."
            delay={350}
          />
          <FeatureCard
            icon={<IconClock className="size-5" />}
            title="Real-time Updates"
            description="Dashboards refresh with the latest data. Make decisions based on current conditions, not stale reports."
            delay={400}
          />
          {/* <FeatureCard
            icon={<IconPuzzle className="size-5" />}
            title="Drag & Drop Builder"
            description="No code required. Build sophisticated dashboards with an intuitive visual interface."
            delay={450}
          /> */}
          <FeatureCard
            icon={<IconShare className="size-5" />}
            title="Export & Share"
            description="Generate shareable links or export to PDF. Perfect for stakeholder presentations and audits."
            delay={500}
          />
        </div>
      </div>

      {/* Use Cases Section */}
      {/* <div 
        className="bg-gradient-to-br from-[#654EDA]/5 to-transparent rounded-[16px] border border-[#654EDA]/10 p-[32px] mb-[48px] animate-in fade-in slide-in-from-bottom-4 duration-500"
        style={{ animationDelay: '550ms', animationFillMode: 'backwards' }}
      >
        <h2 className="text-[18px] font-medium text-[#08090A] mb-[20px] flex items-center gap-[10px]">
          <IconTarget className="size-5 text-[#654EDA]" />
          Purpose-built for FinOps teams
        </h2>

        <div className="grid grid-cols-3 gap-[24px]">
          <div>
            <div className="flex items-center gap-[8px] mb-[8px]">
              <IconChartPie className="size-4 text-[#654EDA]" />
              <span className="text-[14px] font-medium text-[#08090A]">Cost Attribution</span>
            </div>
            <p className="text-[13px] text-[#8A8F98] leading-[20px]">
              Track AI spend by business unit, product line, or cost center. Perfect for chargeback and showback models.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-[8px] mb-[8px]">
              <IconBolt className="size-4 text-[#654EDA]" />
              <span className="text-[14px] font-medium text-[#08090A]">Anomaly Detection</span>
            </div>
            <p className="text-[13px] text-[#8A8F98] leading-[20px]">
              Spot unexpected cost spikes before they impact your budget. Set baselines and track deviations.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-[8px] mb-[8px]">
              <IconUsers className="size-4 text-[#654EDA]" />
              <span className="text-[14px] font-medium text-[#08090A]">Executive Reporting</span>
            </div>
            <p className="text-[13px] text-[#8A8F98] leading-[20px]">
              Create high-level summaries for leadership. Translate technical metrics into business outcomes.
            </p>
          </div>
        </div>
      </div> */}

      {/* Bottom CTA */}
      <div
        className="text-center py-[32px] animate-in fade-in slide-in-from-bottom-4 duration-500"
        style={{ animationDelay: '600ms', animationFillMode: 'backwards' }}
      >
        <p className="text-[14px] text-[#8A8F98] mb-[16px]">
          Join teams who've transformed their AI cost management
        </p>
        <Link to="/organization/billing">
          <Button
            variant="transparent-outline"
            size="lg"
            className="h-[40px] px-[20px] font-medium rounded-[8px] border-[#654EDA] text-[#654EDA] hover:bg-[#654EDA]/5"
          >
            View all plans
            <IconArrowRight className="size-4 ml-[8px]" />
          </Button>
        </Link>
      </div>
    </PageContainer>
  )
}

